import { faArchive, faCircle, faFolderOpen, faLayerGroup, faUser, faUserSlash } from '@fortawesome/pro-solid-svg-icons';
import type { Features } from '~/provides/features.types';
import type { Thread, ThreadFilterParams } from '~/services/threads/threadsClient.types';
import { isLargeTeamSearchRestricted } from '~/utils/team';

type ThreadIdentity = Pick<Thread, 'contact_id' | 'group_id'>;
type SuggestionThread = Pick<Thread, 'status' | 'thread_section'> | null | undefined;

export type ThreadFilters = {
    search: string;
    tag: string;
    group_id: string;
    lifecycle_status?: string;
    membership_status?: string;
};

type FiltersToParamsOptions = {
    includeLifecycleMembershipFilters?: boolean;
};

export const defaultThreadFilters = (): ThreadFilters => ({
    search: '',
    tag: '',
    group_id: '',
});

export const normalizeThreadFilters = (filters: Partial<ThreadFilters> = {}): ThreadFilters => ({
    search: filters.search ?? '',
    tag: filters.tag ?? '',
    group_id: filters.group_id ?? '',
});

export const filtersToParams = (
    filters: ThreadFilters,
    teamId: number,
    { includeLifecycleMembershipFilters = false }: FiltersToParamsOptions = {}
): ThreadFilterParams => {
    const params: ThreadFilterParams = {};
    if (filters.tag) {
        params.tag = filters.tag;
    }
    if (filters.search) {
        const searchKey = isLargeTeamSearchRestricted(teamId)
            ? filters.search.includes('@')
                ? 'email'
                : 'phone'
            : 'search';
        params[searchKey] = filters.search;
    }
    if (includeLifecycleMembershipFilters && filters.lifecycle_status) {
        params.lifecycle_status = filters.lifecycle_status;
    }
    if (includeLifecycleMembershipFilters && filters.membership_status) {
        params.membership_status = filters.membership_status;
    }
    return params;
};

export function canUseMessageHub(): boolean {
    const onCorporate = !Spark.state.currentOrgUnit?.id && !Spark.state.currentGroup?.group_id;

    return onCorporate || !Spark.state.currentTeam.groups_use_corporate_numbers;
}

export function getStatusForInbox(inbox?: string | null): string | undefined {
    if (!inbox || inbox === 'all') {
        return undefined;
    }

    if (inbox === 'me') {
        return 'open';
    }

    if (inbox === 'unassigned') {
        return 'unassigned';
    }

    if (inbox === 'open') {
        return 'open';
    }

    if (inbox === 'closed') {
        return 'closed';
    }

    return undefined;
}

export function isSameThread(a: ThreadIdentity, b: ThreadIdentity): boolean {
    return a.contact_id === b.contact_id && (a.group_id ?? null) === (b.group_id ?? null);
}

export function canUseIntelligentInbox(features: Pick<Features, 'intelligent_inbox'>) {
    if (!features.intelligent_inbox && !Spark.isPlatformAdmin && !Spark.isImpersonating) {
        return false;
    }

    return true;
}

export function shouldShowThreadSuggestion(
    features: Pick<Features, 'simple_message_hub' | 'intelligent_inbox'>,
    thread: SuggestionThread
): boolean {
    if (!thread) {
        return false;
    }

    if (!canUseIntelligentInbox(features)) {
        return false;
    }

    if (
        thread.thread_section === 'pending' ||
        thread.thread_section === 'snoozed' ||
        thread.thread_section === 'completed'
    ) {
        return false;
    }

    if (thread.status === 'closed' && !features.simple_message_hub) {
        return false;
    }

    return true;
}

export const getInboxIcon = (key?: string) => {
    const inboxIcons = {
        me: faUser,
        unassigned: faUserSlash,
        open: faFolderOpen,
        closed: faArchive,
        all: faLayerGroup,
    };

    if (!key || !(key in inboxIcons)) {
        return faCircle;
    }

    return inboxIcons[key as keyof typeof inboxIcons];
};
